<script setup>
import { computed, ref, shallowRef, watchEffect, nextTick, onBeforeUnmount } from 'vue'
import { Head, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import { usePublicDate } from '@/composables/usePublicDate'

defineOptions({ layout: PublicLayout })
const props = defineProps({
  page:       { type: String, required: true },
  updated_at: { type: String, default: '' }
})

const { t, locale } = useI18n()
const { formatDate } = usePublicDate()
const inertiaPage = usePage()

const modules = {
  en: () => import('@/legal/en.js'),
  ru: () => import('@/legal/ru.js'),
  zh: () => import('@/legal/zh.js'),
  th: () => import('@/legal/th.js'),
}

const content = shallowRef(null)
watchEffect(async () => {
  const load = modules[locale.value] || modules.en
  content.value = (await load()).default[props.page]
})

const doc = computed(() => content.value)

// Policy switcher — locale-prefixed links across the four documents.
const current = computed(() => inertiaPage.props.locale || 'en')
function localePath(path) {
  const loc = current.value
  if (!loc || loc === 'en') return path
  return `/${loc}${path}`
}
const PAGES = ['terms', 'refunds', 'privacy', 'company']
const switcher = computed(() => PAGES.map(p => ({
  key: p,
  href: localePath(`/legal/${p}`),
  label: t(`public.legal.${p}`),
  active: p === props.page,
})))

// TOC — headings arrive numbered ("1. About …"); split the number off so the
// navigator can typeset it separately. Unnumbered headings fall back to their
// position. Sections without a heading (intro paragraphs) get no TOC entry.
const tocEntries = computed(() => {
  if (!doc.value) return []
  return doc.value.sections
    .map((s, i) => ({ heading: s.heading, i }))
    .filter(e => e.heading)
    .map(({ heading, i }, pos) => {
      const m = heading.match(/^(\d+)[.、．]\s*(.*)$/)
      return { id: `section-${i + 1}`, num: m ? m[1] : String(pos + 1), text: m ? m[2] : heading }
    })
})

// Scroll-spy — first section intersecting the band just below the sticky
// header wins. Re-observe whenever the locale/page swaps the doc.
const activeId = ref('')
let observer = null
function observeSections() {
  if (observer) observer.disconnect()
  observer = new IntersectionObserver((entries) => {
    const visible = entries.filter(e => e.isIntersecting)
    if (visible.length) activeId.value = visible[0].target.id
  }, { rootMargin: '-96px 0px -55% 0px', threshold: 0 })
  document.querySelectorAll('.legal__section[id]').forEach(el => observer.observe(el))
}
watchEffect(() => { if (doc.value) nextTick(observeSections) })
onBeforeUnmount(() => { if (observer) observer.disconnect() })
</script>

<template>
  <Head><title>{{ doc?.title }} · {{ t('public.head_title') }}</title></Head>

  <BrandHero
    v-if="doc"
    kind="legal"
    :eyebrow="t('public.legal.eyebrow')"
    :title="doc.title"
  >
    <p class="legal-hero__meta">
      {{ t('public.legal.updated', { date: formatDate(updated_at) }) }} · Get Phuket Co., Ltd.
    </p>
    <nav
      class="legal-switcher"
      :aria-label="t('public.legal.eyebrow')"
    >
      <a
        v-for="s in switcher"
        :key="s.key"
        :href="s.href"
        class="legal-switcher__pill"
        :class="{ 'is-active': s.active }"
        :aria-current="s.active ? 'page' : undefined"
      >{{ s.label }}</a>
    </nav>
  </BrandHero>

  <div
    v-if="doc"
    class="legal"
  >
    <div class="legal__inner">
      <!-- Mobile jump menu (hidden ≥900px) -->
      <details
        v-if="tocEntries.length"
        class="legal-jump"
      >
        <summary class="legal-jump__summary">
          {{ t('public.legal.on_this_page') }}
        </summary>
        <ol class="legal-toc__list">
          <li
            v-for="e in tocEntries"
            :key="e.id"
          >
            <a
              :href="`#${e.id}`"
              class="legal-toc__link"
            >
              <span class="legal-toc__num">{{ e.num }}</span>{{ e.text }}
            </a>
          </li>
        </ol>
      </details>

      <div class="legal__cols">
        <aside
          v-if="tocEntries.length"
          class="legal-toc"
          :aria-label="t('public.legal.on_this_page')"
        >
          <p class="legal-toc__label">
            {{ t('public.legal.on_this_page') }}
          </p>
          <ol class="legal-toc__list">
            <li
              v-for="e in tocEntries"
              :key="e.id"
            >
              <a
                :href="`#${e.id}`"
                class="legal-toc__link"
                :class="{ 'is-active': activeId === e.id }"
              >
                <span class="legal-toc__num">{{ e.num }}</span>{{ e.text }}
              </a>
            </li>
          </ol>
        </aside>

        <article class="legal__doc">
          <section
            v-for="(s, i) in doc.sections"
            :id="`section-${i + 1}`"
            :key="i"
            class="legal__section"
          >
            <h2
              v-if="s.heading"
              class="legal__heading"
            >
              {{ s.heading }}
            </h2>
            <p
              v-for="(p, j) in s.paragraphs"
              :key="j"
              class="legal__p"
            >
              {{ p }}
            </p>
            <ul
              v-if="s.items"
              class="legal__list"
            >
              <li
                v-for="(it, k) in s.items"
                :key="k"
              >
                {{ it }}
              </li>
            </ul>
          </section>
        </article>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hero slot content */
.legal-hero__meta { margin: 0.9rem 0 0; font-size: 0.9rem; opacity: 0.78; }
.legal-switcher { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1.4rem; }
.legal-switcher__pill {
  padding: 0.45rem 1rem; border-radius: var(--radius-pill, 999px); text-decoration: none;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 0.9rem;
  color: inherit;
  background: color-mix(in oklch, var(--bg) 14%, transparent);
  border: 1px solid color-mix(in oklch, var(--bg) 40%, transparent);
  transition: background 150ms ease, transform 150ms ease;
}
.legal-switcher__pill:hover { transform: translateY(-2px); background: color-mix(in oklch, var(--bg) 24%, transparent); }
.legal-switcher__pill.is-active { background: var(--bg); color: var(--ink); border-color: var(--bg); }

/* Body */
.legal { background: var(--bg); }
.legal__inner {
  max-width: 96rem; margin: 0 auto;
  padding: clamp(1.5rem, 3vw, 2.5rem) clamp(1.25rem, 4vw, 3rem) 4rem;
}
.legal__cols { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width: 900px) {
  .legal__cols { grid-template-columns: 17rem minmax(0, 46rem); gap: 2.5rem; }
}

/* Sticky "On this page" navigator (desktop) */
.legal-toc { display: none; }
@media (min-width: 900px) {
  .legal-toc {
    display: block; position: sticky; top: 5.5rem; align-self: start;
    background: var(--surface); border: 1px solid var(--line);
    border-radius: 1rem; padding: 1.1rem 1.2rem;
  }
}
.legal-toc__label {
  font-size: 0.72rem; letter-spacing: 0.2em; text-transform: uppercase;
  font-weight: 700; color: var(--muted); margin: 0 0 0.6rem;
}
.legal-toc__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.15rem; }
.legal-toc__link {
  display: flex; gap: 0.6rem; padding: 0.32rem 0.4rem; border-radius: 0.5rem;
  color: var(--inkSoft); text-decoration: none; font-size: 0.9rem; line-height: 1.35;
  transition: color 150ms ease, background 150ms ease;
}
.legal-toc__link:hover { color: var(--ink); background: color-mix(in oklch, var(--ink) 5%, transparent); }
.legal-toc__link.is-active { color: var(--accent); font-weight: 600; }
.legal-toc__num { color: var(--muted); font-variant-numeric: tabular-nums; flex: none; }

/* Mobile jump menu */
.legal-jump {
  margin-bottom: 1.25rem; background: var(--surface); border: 1px solid var(--line);
  border-radius: 1rem; padding: 0.9rem 1.1rem;
}
@media (min-width: 900px) { .legal-jump { display: none; } }
.legal-jump__summary {
  cursor: pointer; font-size: 0.72rem; letter-spacing: 0.2em; text-transform: uppercase;
  font-weight: 700; color: var(--muted); list-style: none;
}
.legal-jump[open] .legal-jump__summary { margin-bottom: 0.6rem; }

/* Document sections */
.legal__doc { display: grid; gap: 1rem; min-width: 0; }
.legal__section {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 1rem; padding: 1.4rem; scroll-margin-top: 5.5rem;
}
.legal__heading {
  font-family: var(--font-display, var(--display)); font-weight: 800;
  letter-spacing: -0.01em; font-size: 1.15rem; color: var(--ink); margin: 0 0 0.6rem;
}
.legal__p { color: var(--inkSoft); line-height: 1.65; margin: 0 0 0.75rem; white-space: pre-wrap; }
.legal__p:last-child { margin-bottom: 0; }
.legal__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; }
.legal__list li { position: relative; padding-left: 1.4rem; color: var(--inkSoft); line-height: 1.55; }
.legal__list li::before { content: "•"; position: absolute; left: 0; font-weight: 700; color: var(--accent); }
</style>
